import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API } from '@/config/api';
import { toast } from 'sonner';

export const useActiveTracking = (user) => {
    const [activeTask, setActiveTask] = useState(null);
    const [loading, setLoading] = useState(true);
    const [lastCheckTime, setLastCheckTime] = useState(Date.now());

    const fetchActiveTask = useCallback(async () => {
        if (!user?.id) return;
        try {
            const res = await axios.get(`${API}/tracking/active/${user.id}`);
            setActiveTask(res.data);
        } catch (err) {
            console.error("Error fetching active task:", err);
        } finally {
            setLoading(false);
        }
    }, [user?.id]);

    const triggerDeadlineNotification = useCallback((task) => {
        // Chrome Notification
        if (Notification.permission === "granted") {
            new Notification("MAFIA ALERT 🤌", {
                body: `Compare! Dovevi finire "${task.description}" ORA! Il Don non ama aspettare...`,
                icon: "/favicon.png" // Fallback to a generic icon if needed
            });
        }

        // Sonner toast with sound
        toast.error("⚠️ TEMPO SCADUTO!", {
            description: `Il Don sta arrivando... Hai finito "${task.description}"? Registra o soffri! 💀`,
            duration: 10000,
        });

        // Play scary sound
        try {
            const audio = new Audio('/sounds/threat.mp3');
            audio.play().catch(e => console.warn("Autoplay blocked:", e));
        } catch (e) {
            console.warn("Could not play sound:", e);
        }
    }, []);

    useEffect(() => {
        fetchActiveTask();

        // Poll every minute to check for deadline reached
        const interval = setInterval(() => {
            setLastCheckTime(Date.now());
            if (activeTask && activeTask.status === 'active') {
                const endTime = new Date(activeTask.estimated_end_time).getTime();
                const now = Date.now();

                if (now >= endTime) {
                    // Deadline reached!
                    triggerDeadlineNotification(activeTask);
                }
            }
        }, 60000);

        return () => clearInterval(interval);
    }, [fetchActiveTask, activeTask, triggerDeadlineNotification]);

    const startTracking = async (taskData) => {
        try {
            const res = await axios.post(`${API}/tracking/start`, {
                user_id: user.id,
                user_name: user.name,
                ...taskData
            });
            setActiveTask(res.data.tracking);
            return res.data;
        } catch (err) {
            toast.error(err.response?.data?.detail || "Errore nell'avvio del tracking");
            throw err;
        }
    };

    const finishTracking = async () => {
        if (!activeTask) return;
        try {
            const res = await axios.post(`${API}/tracking/finish/${activeTask.id}`);
            setActiveTask(null);
            toast.success("MAGNIFICO!", {
                description: `Hai completato il lavoro! ${res.data.hours_logged} ore registrate.`,
            });
            return res.data;
        } catch (err) {
            toast.error("Errore nella chiusura del task");
            throw err;
        }
    };

    // Request notification permission on mount
    useEffect(() => {
        if (Notification.permission === "default") {
            Notification.requestPermission();
        }
    }, []);

    return { activeTask, loading, startTracking, finishTracking, refetch: fetchActiveTask };
};
